import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Github, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { personalInfo } from "../data/mock";

const FloatingBlock = ({ children, delay, x, y, duration = 20 }) => (
  <motion.div
    className="absolute pointer-events-none select-none"
    style={{ left: `${x}%`, top: `${y}%` }}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{
      opacity: [0.15, 0.35, 0.15],
      scale: [0.95, 1.05, 0.95],
      y: [-10, 10, -10],
    }}
    transition={{
      duration: duration,
      repeat: Infinity,
      delay: delay,
      ease: "easeInOut",
    }}
  >
    {children}
  </motion.div>
);

const CodeLine = ({ text, color = "text-emerald-600/40 dark:text-emerald-400/40" }) => (
  <div className={`font-mono text-xs ${color} whitespace-nowrap`}>{text}</div>
);

const HeroSection = () => {
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width - 0.5;
      const cy = (e.clientY - rect.top) / rect.height - 0.5;
      mouseX.set(cx * 20);
      mouseY.set(cy * 20);
    };

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReduced) {
      window.addEventListener("mousemove", handleMove);
      return () => window.removeEventListener("mousemove", handleMove);
    }
  }, [mouseX, mouseY]);

  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500"
      aria-label="Introduction"
      itemScope
      itemType="https://schema.org/Person"
    >
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.04)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* Radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[120px]" />

      {/* Floating code elements */}
      {mounted && (
        <motion.div
          style={{ x: springX, y: springY }}
          className="absolute inset-0"
        >
          <FloatingBlock x={5} y={15} delay={0} duration={25}>
            <div className="bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/40 rounded-lg px-3 py-2 backdrop-blur-sm">
              <CodeLine text="// WordPress Core" />
              <CodeLine text='apply_filters( "the_content" );' color="text-zinc-400/50 dark:text-zinc-500/50" />
            </div>
          </FloatingBlock>

          <FloatingBlock x={75} y={10} delay={2} duration={22}>
            <div className="bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/40 rounded-lg px-3 py-2 backdrop-blur-sm">
              <CodeLine text="<InnerBlocks />" />
              <CodeLine text="// Gutenberg Block" color="text-zinc-400/50 dark:text-zinc-500/50" />
            </div>
          </FloatingBlock>

          <FloatingBlock x={85} y={55} delay={4} duration={28}>
            <div className="bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/40 rounded-lg px-3 py-2 backdrop-blur-sm">
              <CodeLine text="$ wp core update" />
              <CodeLine text="Success: Updated." color="text-emerald-600/40 dark:text-emerald-500/40" />
            </div>
          </FloatingBlock>

          <FloatingBlock x={10} y={70} delay={3} duration={24}>
            <div className="bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/40 rounded-lg px-3 py-2 backdrop-blur-sm">
              <CodeLine text="git commit -m 'fix:'" />
              <CodeLine text={"merged to trunk \u2713"} color="text-emerald-600/40 dark:text-emerald-500/40" />
            </div>
          </FloatingBlock>

          <FloatingBlock x={60} y={80} delay={5} duration={26}>
            <div className="bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-zinc-800/40 rounded-lg px-3 py-2 backdrop-blur-sm">
              <CodeLine text="registerBlockType(" />
              <CodeLine text="  'core/paragraph'" color="text-zinc-400/50 dark:text-zinc-500/50" />
              <CodeLine text=");" />
            </div>
          </FloatingBlock>
        </motion.div>
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-24 pb-20">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {/* Text content - 3 cols */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
                <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-wide">
                  Open Source Engineer
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-zinc-900 dark:text-zinc-50 leading-[1.1] tracking-tight" itemProp="name">
                Hi, I'm{" "}
                <span className="relative">
                  <span className="relative z-10">{personalInfo.name.split(" ")[0]}</span>
                  <motion.span
                    className="absolute bottom-1 left-0 right-0 h-3 bg-emerald-500/20 rounded-sm -z-0"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    style={{ originX: 0 }}
                  />
                </span>{" "}
                <span className="text-zinc-400 dark:text-zinc-400">{personalInfo.name.split(" ")[1]}</span>
              </h1>

              <p className="mt-6 text-lg sm:text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-xl" itemProp="description">
                <span itemProp="jobTitle">{personalInfo.role}</span> at{" "}
                <span className="text-zinc-900 dark:text-zinc-200 font-medium" itemProp="worksFor">{personalInfo.company}</span>,
                building enterprise WordPress experiences and contributing to the
                open-source software that powers the web.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  onClick={() => scrollToSection("#opensource")}
                  className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white dark:text-zinc-950 font-semibold px-6 h-11 text-sm rounded-lg group"
                >
                  View Open Source Work
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100 h-11 px-6 text-sm rounded-lg"
                >
                  <a
                    href={personalInfo.links.github}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Github className="w-4 h-4 mr-2" />
                    GitHub
                  </a>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100 h-11 px-6 text-sm rounded-lg"
                >
                  <a
                    href={personalInfo.links.wordpress}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    WordPress.org
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Profile image - 2 cols */}
          <motion.div
            className="lg:col-span-2 flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="relative">
              {/* Decorative ring */}
              <div className="absolute -inset-4 rounded-full border border-emerald-500/10 animate-[spin_30s_linear_infinite]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-emerald-500/60 dark:bg-emerald-400/60" />
              </div>
              <div className="absolute -inset-8 rounded-full border border-zinc-300/30 dark:border-zinc-800/30 animate-[spin_45s_linear_infinite_reverse]">
                <div className="absolute bottom-0 right-0 w-1.5 h-1.5 rounded-full bg-zinc-400/40 dark:bg-zinc-500/40" />
              </div>

              {/* Image container */}
              <div className="w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden border-2 border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 relative">
                <img
                  src={personalInfo.profileImage}
                  alt={`${personalInfo.name} - ${personalInfo.role} at ${personalInfo.company}`}
                  className="w-full h-full object-cover"
                  itemProp="image"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-zinc-100 dark:to-zinc-900 items-center justify-center hidden"
                  style={{ display: "flex" }}
                >
                  <span className="text-5xl font-bold text-emerald-600/60 dark:text-emerald-400/60 font-mono">
                    HB
                  </span>
                </div>
              </div>

              {/* Status badge */}
              <motion.div
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-full px-4 py-1.5 flex items-center gap-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                  Available for collaboration
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <motion.div
            className="w-6 h-10 rounded-full border-2 border-zinc-300 dark:border-zinc-700 flex justify-center pt-2"
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-1 h-2 rounded-full bg-emerald-500/60 dark:bg-emerald-400/60" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
